/**
 * Camp Highlands division attribution for camper-facing rollups.
 *
 * The reportService rule: a camper's *own* Division (CamperDetails.DivisionID,
 * surfaced as `Enrollment.division.name` once joined) takes precedence over
 * any inference from their cabin's Bunk Area. This matters for Rangers, who
 * sleep in Senior-range cabins during 1st Term but operationally count as
 * Rangers, not Seniors.
 *
 * On the 1st Term and 2nd Term views the Rangers cohort is dropped entirely
 * from the 4-standard-band totals so we don't double-count them. They reappear
 * on the Rangers session view, which renders only the Rangers band.
 */
import type { Camper, Enrollment } from "../types";
import {
  divisionForCabinNumber,
  normalizeDivisionName,
  parseCabinNumber,
  type CampHighlandsDivision,
} from "./terminology";
import type { SessionLabel } from "../context/SeasonContext";

/**
 * Find the camper's enrollment for the given term, preferring an exact term
 * match. Returns undefined when the camper has no row for that term.
 */
export function enrollmentForTerm(
  camper: Camper,
  termId: number | null | undefined,
): Enrollment | undefined {
  if (termId == null) return camper.enrollments?.[0];
  return camper.enrollments?.find((e) => e.termId === termId);
}

/**
 * Resolve the Camp Highlands division for a single camper in the active
 * session. Returns null when the camper falls outside the standard model
 * (e.g. cabin number is not in 1-26 and they aren't a Rangers camper).
 *
 * On the Rangers session view, only Rangers-division campers are attributed
 * (everything else returns null). On the 1st/2nd Term views, Rangers-division
 * campers return null so they don't pollute the Seniors/Juniors/Colts/Cubs
 * totals — they reappear in the Rangers view via the converse branch.
 */
export function attributeCamperToDivision(
  camper: Camper,
  termId: number | null | undefined,
  session: SessionLabel,
): CampHighlandsDivision | null {
  const enrollment = enrollmentForTerm(camper, termId);
  const camperDivisionName = normalizeDivisionName(enrollment?.division?.name ?? "");
  const isRangersCamper = /^rangers$/i.test(camperDivisionName);

  if (session === "rangers") {
    return isRangersCamper ? "Rangers" : null;
  }

  // 1st Term / 2nd Term view: drop Rangers-division campers from the totals.
  if (isRangersCamper) return null;

  const cabin = enrollment?.overrideCabin ?? enrollment?.cabin;
  return divisionForCabinNumber(parseCabinNumber(cabin?.name ?? ""));
}
